/*
 * Route-driven state container for a singular Project
 */

import PropTypes from 'prop-types';
import { cloneElement, Component } from 'react';
import _ from 'underscore';
import ProjectActions from '../../actions/ProjectActions';
import ProjectStore from '../../stores/ProjectStore';
import utils from '../../utils';

/*
 * Loads and handles state updates for a single project
 * @param params { project_id: }
 **/
class ProjectProvider extends Component<
  {
    params: {
      project_id: string;
      reference_type?: string;
      reference?: string;
    };
    children: any;
    location?: any;
  },
  any
> {
  static propTypes = {
    params: PropTypes.object.isRequired,
    children: PropTypes.node,
  };

  constructor(props) {
    super(props);

    this.state = {
      status: {
        loading: true,
      },
      data: {},
      isSuper: true,
      isOwner: false,
    };
  }

  unsubscribeProject;

  componentDidMount() {
    this.unsubscribeProject = (ProjectStore as any).listen(this.onProjectUpdate);

    const cachedProject = ProjectStore.getProject(utils.nuc(this.props.params.project_id));
    if (cachedProject) {
      this.onProjectUpdate({ [utils.nuc(this.props.params.project_id)]: cachedProject });
      return;
    }
    ProjectActions.load(
      this.props.params.project_id,
      _.extend(
        {
          ref_type: this.props.params.reference_type,
          ref: this.props.params.reference,
        },
        this.props.location && this.props.location.query && this.props.location.query.ref
          ? { badge_ref: this.props.location.query.ref }
          : {},
      ),
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.params.project_id !== this.props.params.project_id) {
      // note this won't reload on branch change --
      // but we don't currently use that branch data either
      const cachedProject = ProjectStore.getProject(utils.nuc(nextProps.params.project_id));
      if (cachedProject) {
        // We need this function call to happen on the next tick because onProjectUpdate
        // will look for info for the project_id from this.props, which hasn't been
        // set to nextProps yet.
        setTimeout(this.onProjectUpdate, 0, { [utils.nuc(nextProps.params.project_id)]: cachedProject });
        return;
      }
      ProjectActions.load(
        nextProps.params.project_id,
        _.extend(
          {
            ref_type: 'branch',
            ref: nextProps.params.reference,
          },
          nextProps.location && nextProps.location.query && nextProps.location.query.ref
            ? { badge_ref: nextProps.location.query.ref }
            : {},
        ),
      );
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeProject) this.unsubscribeProject();
  }

  onProjectUpdate = projects => {
    const project = projects[utils.nuc(this.props.params.project_id)];
    if (!project || !project.data) {
      return;
    }
    if (!project.data.locator) {
      // When project_id is undefined, data turns out to be the entire projects object from the ProjectStore.
      // In this case, we are still loading a project.
      this.setState({
        status: { loading: true },
        data: null,
      });
    } else {
      this.setState({
        status: project.status,
        data: project.data,
      });
    }
  };

  render() {
    return cloneElement(
      this.props.children,
      _.omit(
        _.extend({}, this.props, {
          project: this.state,
        }),
        'children',
      ),
    );
  }
}

export default ProjectProvider;
